// src/firebaseClient.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut as fbSignOut,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  deleteDoc,
  serverTimestamp,
  startAfter,
  onSnapshot,
  limit,
  limit as qLimit,
  arrayUnion,
  Timestamp,
  writeBatch,
  documentId,
  addDoc,
  where as qWhere
} from 'firebase/firestore';
import {
  doc as docRefRootRaids,
  deleteDoc as deleteRootDoc,
} from 'firebase/firestore';

let app, auth, db;

// 1) firebaseConfig을 파일 내부에서 관리 (.env 권장)
const defaultConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const sevenDaysFromNow = () =>
  Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

// 외부에서 override 가능하지만, 안 넘겨줘도 동작하도록 기본값 사용
export function initFirebase(configOverride = {}) {
  if (app) return { app, auth, db };
  const config = { ...defaultConfig, ...configOverride };
  app = initializeApp(config);
  auth = getAuth(app);
  db = getFirestore(app);
  return { app, auth, db };
}

export function watchAuth(callback) {
  return onAuthStateChanged(auth, callback);
}
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  await signInWithPopup(auth, provider);
}
export async function signOut() {
  await fbSignOut(auth);
}

/* ============================
 *  User Raids
 * ============================ */

export async function saveRaidCloud(uid, raid) {
  const db = getFirestore();
  // 1) 내 하위 컬렉션
  await setDoc(
    doc(db, 'users', uid, 'raids', raid.id),
    {
      ...raid,
      uid,
      updatedAt: serverTimestamp(),
      createdAt: raid.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
  // 2) 공개 컬렉션(고정 id 사용)
  const pubId = `${uid}_${raid.id}`;
  await setDoc(
    doc(db, 'raids_public', pubId),
    {
      ...raid,
      originUserId: uid,
      originRaidId: raid.id,
      updatedAt: serverTimestamp(),
      createdAt: raid.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
}

export async function deleteRaidCloud(uid, raidId) {
  const db = getFirestore();
  await deleteDoc(doc(db, 'users', uid, 'raids', raidId));
  // 공개 컬렉션도 같이 삭제
  const pubId = `${uid}_${raidId}`;
  await deleteDoc(doc(db, 'raids_public', pubId));
}

export async function getMyRaids(uid) {
  const ref = collection(db, 'users', uid, 'raids');
  // 저장 스키마에 맞는 필드로 정렬(기존 timestamp → updatedAt)
  const q = query(ref, orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // 보정: updatedAt 없거나 Timestamp/문자열 혼재 대비
  const toMillis = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v === 'string') return Date.parse(v) || 0;
    return 0;
  };
  return rows.sort((a, b) => {
    const aKey = toMillis(a.updatedAt) || toMillis(a.createdAt) || Date.parse(a.timestamp) || 0;
    const bKey = toMillis(b.updatedAt) || toMillis(b.createdAt) || Date.parse(b.timestamp) || 0;
    return bKey - aKey; // desc
  });
}

export async function getMyRaidsByRaidAndJob(uid, raidName, job) {
  const ref = collection(db, 'users', uid, 'raids');
  const q = query(
    ref,
    where('raidName', '==', raidName),
    where('job', '==', job)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// 2) 전체 집계: collectionGroup('raids') 사용 (users/*/raids 전부)
// 전체 집계: raids_public 사용 + limit/페이지네이션 + 최신순
export async function getAllRaids({
  raidName,
  job,
  take = 100,        // 최초 읽기 제한
  cursor = null,     // 페이지네이션용 마지막 문서 스냅샷
  updatedAfter = null, // 선택: 최근 것만 (Timestamp)
} = {}) {
  const db = getFirestore();
  const base = collection(db, 'raids_public');

  const conds = [];
  if (raidName) conds.push(where('raidName', '==', raidName));
  if (job) conds.push(where('job', '==', job));
  if (updatedAfter) conds.push(where('updatedAt', '>=', updatedAfter));

  const order = orderBy('updatedAt', 'desc');

  let qy = query(base, ...conds, order, limit(take));
  if (cursor) qy = query(base, ...conds, order, startAfter(cursor), limit(take));

  const snap = await getDocs(qy);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

  return { items, cursor: nextCursor };
}


export async function deleteRaidInRoot(raidId) {
  const ref = docRefRootRaids(db, 'raids', raidId);
  await deleteRootDoc(ref);
}

/* ============================
 *  Party (공격대) APIs
 * ============================ */

// 8자리: 대문자 A-Z + 숫자 0-9
const PARTY_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const PARTY_CODE_LEN = 6;

export function genPartyCode(len = PARTY_CODE_LEN) {
  let out = '';
  for (let i = 0; i < len; i++) {
    out += PARTY_ALPHABET[Math.floor(Math.random() * PARTY_ALPHABET.length)];
  }
  return out;
}

// 코드 형식 검증: 허용 문자만 + 8자
export function isValidPartyCode(code = '') {
  if (typeof code !== 'string' || code.length !== PARTY_CODE_LEN) return false;
  return [...code.toUpperCase()].every((ch) => PARTY_ALPHABET.includes(ch));
}
/**
 * 공격대 생성
 * - parties/{code} 문서 생성
 * - parties/{code}/members/{ownerUid} 멤버 문서(leader) 생성
 * - 만료일: 7일 후(expireAt) — 콘솔에서 TTL 정책으로 자동 삭제 설정 권장
 * - ⚠️ 읽기 0회: 코드를 8자로 늘려 충돌확률을 사실상 제거하고 중복체크(getDoc) 생략
 */
export async function createParty({
  ownerUid,
  raidName,
  capacity,
  nickname,
  job,
  engraving,
}) {
  const db = getFirestore();

  // 0-read 전략: 충돌체크 없이 바로 발급
  const code = genPartyCode(PARTY_CODE_LEN);

  // TTL 대상: Firestore Timestamp 로 명시
  const expireAt = Timestamp.fromDate(
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );

  await setDoc(doc(db, 'parties', code), {
    ownerUid,
    raidName,
    capacity,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    expireAt, // TTL 사용 시 이 필드 기준
    status: 'active',
    memberUids: [ownerUid],
  });

  await setDoc(doc(db, 'parties', code, 'members', ownerUid), {
    nickname,
    job,
    engraving,
    role: 'leader',
    joinedAt: serverTimestamp(),
    expireAt, // 파티와 동일 만료시각
  });

  return code;
}

/**
 * 공격대 참여
 * - parties/{code}/members/{uid} upsert (role: member)
 */
export async function joinParty({ code, uid, nickname, job, engraving }) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();

  if (!isValidPartyCode(upper)) {
    throw new Error('공격대 코드 형식이 올바르지 않습니다.');
  }
  const partyRef = doc(db, 'parties', upper);
  const partySnap = await getDoc(partyRef); // 존재 확인 1회(필수)
  if (!partySnap.exists()) throw new Error('존재하지 않는 공격대 코드입니다.');
  const parentExpireAt = partySnap.data()?.expireAt ?? sevenDaysFromNow();

  await setDoc(
    doc(partyRef, 'members', uid),
    {
      nickname,
      job,
      engraving,
      role: 'member',
      joinedAt: serverTimestamp(),
      expireAt: parentExpireAt,
    },
    { merge: true }
  );

  // ★ 파티 메타에 나를 추가(중복 안전)
  await setDoc(
    partyRef,
    {
      memberUids: arrayUnion(uid),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
    },
    { merge: true }
  );

  return upper;
}

/**
 * 공격대 정보 + 내 멤버 정보 단건 조회 (페이지 진입 시 편의, 읽기 2회)
 */
export async function fetchPartyAndMe(code, uid) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();

  const partyRef = doc(db, 'parties', upper);
  const partyDocSnap = await getDoc(partyRef);
  if (!partyDocSnap.exists()) throw new Error('공격대가 존재하지 않습니다.');
  const meDocSnap = await getDoc(doc(partyRef, 'members', uid));

  return {
    party: { id: upper, ...partyDocSnap.data() },
    me: meDocSnap.exists() ? { id: meDocSnap.id, ...meDocSnap.data() } : null,
  };
}

/**
 * 공격대 기록 저장 (1인 1회)
 * - parties/{code}/analyses/{uid} : 문서ID=uid로 생성
 * - users/{uid}/analyses/*        : 개인 히스토리(선택)
 * Firestore 규칙에서 update 금지/존재 시 차단으로 1회 보장
 */
export async function savePartyAnalysis({
  code,
  uid,
  nickname,
  job,
  engraving,
  power,                  // ← 추가
  level,                  // ← 추가
  raidName,
  payload,                // payload.memo 포함
  alsoSavePersonal = true,
  docId,                  // ← 선택: 기존 문서 갱신용
}) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();

  // undefined 제거 유틸
  const prune = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));

  // 파티 분석 문서: 기본은 uid로, 덮어쓰기는 docId 사용
  const partyDocId = docId || uid;

  // top-level에도 power/memo를 꺼내서 같이 저장 (목록 접근/검색 편의)
  const topMemo = (payload?.memo ?? '').toString().trim() || undefined;
  const topPower = (typeof power === 'number' && Number.isFinite(power)) ? power : undefined;
  const topLevel = (typeof level === 'number' && Number.isFinite(level)) ? level : undefined;

  await setDoc(
    doc(db, 'parties', upper, 'analyses', partyDocId),
    prune({
      authorUid: uid,
      nickname,
      job,
      engraving,
      power: topPower,          // ← 추가(상단)
      level: topLevel,          // ← 추가(상단)
      memo: topMemo,            // ← 추가(상단)
      createdAt: serverTimestamp(),
      expireAt: sevenDaysFromNow(),
      payload,                  // payload.memo도 그대로 보존
    }),
    { merge: true }             // ← 기존 문서가 있으면 갱신
  );

  if (alsoSavePersonal) {
    await setDoc(
      doc(collection(db, 'users', uid, 'analyses')),
      prune({
        partyCode: upper,
        raidName,
        power: topPower,        // ← 개인 기록에도 보관(검색/집계용)
        level: topLevel,        // ← 개인 기록에도 보관(검색/집계용)
        memo: topMemo,          // ← 옵션: 개인 목록에서도 바로 보려면 유용
        payload,
        createdAt: serverTimestamp(),
      }),
      { merge: false }
    );
  }
}

/**
 * 공격대 기록 목록 조회 (수동 새로고침용)
 */
export async function fetchPartyAnalyses(code) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();
  const ref = collection(db, 'parties', upper, 'analyses');
  const q = query(ref, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * 공대장: 기록 비우기
 * - 정렬/전체 로드 없이 batchDeleteAll 재사용로 읽기 최소화
 */
export async function clearPartyAnalysesClient(code) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();
  const partyRef = doc(db, 'parties', upper);
  await batchDeleteAll(collection(partyRef, 'analyses'));
}

/* -------- 대량 삭제 유틸 (batch 500 제한 대응) -------- */
async function batchDeleteAll(colRef, chunk = 300) {
  const db = getFirestore();
  // chunk 단위로 반복 삭제
  // composite index 불필요하도록 단순 limit만 사용
  while (true) {
    const snap = await getDocs(query(colRef, limit(chunk)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    if (snap.size < chunk) break;
  }
}

/**
 * 공대장: 공격대 해산
 * - analyses, members, gate_logs, averages 삭제 후 parties/{code} 삭제
 * - 대량 문서 안전 삭제(batchDeleteAll) 적용
 */
export async function disbandPartyClient(code) {
  const db = getFirestore();
  const id = String(code || '').toUpperCase();
  const partyRef = doc(db, 'parties', id);

  // 이 파티에서 실제로 쓰는 하위 컬렉션
  const SUBCOLS = ['analyses', 'members', 'gate_logs', 'averages'];

  // 하위 컬렉션들 모두 삭제
  await Promise.all(
    SUBCOLS.map((name) => batchDeleteAll(collection(partyRef, name)))
  );

  // 마지막에 파티 문서 삭제
  await deleteDoc(partyRef);
}

/**
 * 평균 스냅샷 저장 (parties/{code}/averages/*, auto-id)
 */
export async function savePartyAverageSnapshot({
  code,
  gateName,
  battleTimeAvg,
  dealerCount,
  avgTotalRaw,
  avgDpsRaw,
  createdBy, // { uid, nickname, job, engraving } (옵션)
}) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();

  const colRef = collection(db, 'parties', upper, 'averages');
  // auto-id 문서
  const docRef = doc(colRef);

  await setDoc(docRef, {
    createdAt: serverTimestamp(),
    createdBy: createdBy
      ? {
          uid: createdBy.uid ?? null,
          nickname: createdBy.nickname ?? '',
          job: createdBy.job ?? '',
          engraving: createdBy.engraving ?? '',
        }
      : null,
    // PartyPage가 기대하는 구조와 동일하게 payload 안에 담습니다.
    payload: {
      type: '__avg__',
      gateName,
      battleTimeAvg,
      dealerCount,
      avgTotalRaw,
      avgDpsRaw,
    },
    expireAt: sevenDaysFromNow(),
  });

  return docRef.id;
}

/**
 * ✅ 관문 설정: 파티 메타 + gate_logs 에 기록
 * (옵션) 레거시 analyses 기록은 기본 꺼둠
 */
export async function savePartyGateSelection({
  code,
  uid,
  nickname,
  job,
  engraving,
  gateName,
  alsoWriteLegacyAnalysis = false,
}) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();

  // 1) 파티 메타에 현재 관문 저장
  await setDoc(
    doc(db, 'parties', upper),
    {
      gateName,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
    },
    { merge: true }
  );

  // 2) 전용 로그 서브콜렉션에 변경 이력 저장
  await setDoc(doc(collection(db, 'parties', upper, 'gate_logs')), {
    authorUid: uid,
    nickname,
    job,
    engraving,
    gateName,
    type: '__gate__', // 식별 태그(선택)
    createdAt: serverTimestamp(),
    expireAt: sevenDaysFromNow(),
  });

  // 3) (옵션) 레거시: analyses 에도 한 건 남겨 기존 코드와 호환
  if (alsoWriteLegacyAnalysis) {
    await setDoc(doc(collection(db, 'parties', upper, 'analyses')), {
      authorUid: uid,
      nickname,
      job,
      engraving,
      createdAt: serverTimestamp(),
      payload: { type: '__gate__', gateName },
      expireAt: sevenDaysFromNow(),
    });
  }
}

// 현재 관문(메타) 읽기 (단건 읽기 1회)
export async function getPartyGateName(code) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();
  const snap = await getDoc(doc(db, 'parties', upper));
  return snap.exists() ? snap.data().gateName || '' : '';
}

// 최신 관문 로그 1건(gate_logs)
export async function fetchLatestGateLog(code) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();
  const q = query(
    collection(db, 'parties', upper, 'gate_logs'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  const d = snap.docs[0];
  return d ? { id: d.id, ...d.data() } : null;
}

/**
 * 내가 속한 파티 실시간 구독 (memberUids 기반) — 읽기/네트워크 비용 최소화
 * ⚠️ 복합 인덱스 필요:
 *   where(memberUids array-contains) + orderBy(updatedAt desc)
 *   firestore.indexes.json 예:
 *   {
 *     "indexes": [
 *       {
 *         "collectionGroup": "parties",
 *         "queryScope": "COLLECTION",
 *         "fields": [
 *           { "fieldPath": "memberUids", "arrayConfig": "CONTAINS" },
 *           { "fieldPath": "updatedAt", "order": "DESCENDING" }
 *         ]
 *       }
 *     ]
 *   }
 */
export function subscribeMyParties(uid, cb) {
  if (!uid) return () => {};
  const db = getFirestore();
  const col = collection(db, 'parties');
  return onSnapshot(
    query(
      col,
      where('status', '==', 'active'),               // ✨ 휴면 파티 제외
      where('memberUids', 'array-contains', uid),
      orderBy('updatedAt', 'desc'),
      limit(50) // 필요시 파라미터화 가능
    ),
    (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(arr);
    }
  );
}

/**
 * 최신 전투분석 기록(__rec__)만 가져오기
 * ⚠️ 인덱스 권장: payload.type + createdAt(desc)
 * ⚙️ 최적화:
 *   - 신 스키마 쿼리 결과 0건이면, 우선 존재만 limit(1)로 확인(빈 컬렉션이면 여기서 종료)
 *   - 존재가 확인될 때만 레거시 스키마 fallback 쿼리 수행
 */
export async function fetchPartyRecords(code, { limit: take = 200 } = {}) {
  const upper = String(code || '').toUpperCase();
  const colRef = collection(db, 'parties', upper, 'analyses');

  try {
    // 1) 신 스키마 우선
    const q1 = query(
      colRef,
      where('payload.type', '==', '__rec__'),
      orderBy('createdAt', 'desc'),
      qLimit(take)
    );
    const snap1 = await getDocs(q1);
    const out = snap1.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (out.length) return out;

    // 2) 빈 컬렉션이면 즉시 종료 (여기까지 총 1~2 reads)
    const existSnap = await getDocs(query(colRef, qLimit(1)));
    if (existSnap.empty) return [];

    // 3) 레거시만 있는 경우에만 fallback
    const q2 = query(colRef, orderBy('createdAt', 'desc'), qLimit(take));
    const snap2 = await getDocs(q2);
    return snap2.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => !r?.payload?.type || r?.payload?.type === '__rec__');
  } catch (err) {
    throw err;
  }
}

/* ============================
 *  Backfill / Utilities
 * ============================ */

// 임시 실행용(관리자 계정에서만)
export async function backfillMemberUidsOnce() {
  const db = getFirestore();
  const parties = await getDocs(collection(db, 'parties'));
  for (const p of parties.docs) {
    const mSnap = await getDocs(collection(db, 'parties', p.id, 'members'));
    const uids = new Set();
    mSnap.forEach((m) => {
      const d = m.data();
      if (d?.uid) uids.add(d.uid); // 멤버 문서에 uid 필드가 있다면
      uids.add(m.id); // 문서ID가 uid라면 이것만으로도 충분
    });
    if (uids.size > 0) {
      await setDoc(
        p.ref,
        {
          memberUids: Array.from(uids),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  }
}

// 개발 중 임시 노출 (프로덕션에서는 노출 금지!)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.__backfillMemberUidsOnce = backfillMemberUidsOnce;
}

/**
 * ensure 계열: 존재/멤버십 보장 + TTL 반영
 */
export async function ensureJoinParty({ code, uid, profile }) {
  const db = getFirestore();
  const upper = String(code || '').toUpperCase();
  const partyRef = doc(db, 'parties', upper);
  const partySnap = await getDoc(partyRef);
  if (!partySnap.exists()) return { status: 'not_found' };

  const memberRef = doc(partyRef, 'members', uid);
  const memberSnap = await getDoc(memberRef);

  if (memberSnap.exists()) {
    // 존재하면 메타 최신화 정도만
    await setDoc(
      partyRef,
      {
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { status: 'already', party: partySnap.data() };
  }

  const parentExpireAt = partySnap.data()?.expireAt ?? sevenDaysFromNow();
  await setDoc(
    memberRef,
    {
      ...profile, // nickname, job, engraving 등
      joinedAt: serverTimestamp(),
      expireAt: parentExpireAt,
    },
    { merge: true }
  );

  // ★ subscribeMyParties가 memberUids 기반이라 반드시 올려줘야 함
  await setDoc(
    partyRef,
    {
      memberUids: arrayUnion(uid),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { status: 'joined', party: partySnap.data() };
}

export async function ensurePartyExpireAt(code, createdAt) {
  const created =
    typeof createdAt?.toMillis === 'function'
      ? new Date(createdAt.toMillis())
      : typeof createdAt?.seconds === 'number'
      ? new Date(createdAt.seconds * 1000)
      : new Date(); // 혹시 없으면 지금 시각
  const expire = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
  await setDoc(
    doc(db, 'parties', code),
    {
      // createdAt이 아예 없던 오래된 문서도 보정
      createdAt: createdAt ?? serverTimestamp(),
      expireAt: Timestamp.fromDate(expire),
    },
    { merge: true }
  );
}

// 파티 메타 gateName 실시간 구독
export function subscribePartyGateName(code, cb) {
  const db = getFirestore();
  const upper = String(code || "").toUpperCase();
  return onSnapshot(doc(db, "parties", upper), (snap) => {
    cb(snap.exists() ? (snap.data().gateName || "") : "");
  });
}

// averages 서브컬렉션 실시간 구독 (최신순, 개수 제한)
export function subscribePartyAverages(code, cb, lim = 20) {
  const db = getFirestore();
  const upper = String(code || "").toUpperCase();
  const col = collection(db, "parties", upper, "averages");
  const qy = query(col, orderBy("createdAt", "desc"), limit(lim));
  return onSnapshot(qy, (snap) => {
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    cb(arr);
  });
}

export async function extendPartyExpireAtDeep({
  code,
  days = 7,
  cols = ['gate_logs', 'averages', 'members', 'analyses'],
}) {
  const db = getFirestore();
  const id = String(code || '').toUpperCase();
  const partyRef = doc(db, 'parties', id);

  const MS_DAY = 24 * 60 * 60 * 1000;
  const toMillis = (v) => {
    if (!v) return null;
    if (typeof v.toMillis === 'function') return v.toMillis();        // Firestore Timestamp
    if (typeof v.seconds === 'number') return v.seconds * 1000;       // plain {seconds,nanos}
    if (v instanceof Date) return v.getTime();
    return null;
  };

  // 1) 파티 메타 읽기(1 read) → 기존 expireAt 기준으로 +days
  const partySnap = await getDoc(partyRef);
  if (!partySnap.exists()) throw new Error('파티가 존재하지 않습니다.');

  const nowMs = Date.now();
  const partyExpireMs0 = toMillis(partySnap.data()?.expireAt) ?? nowMs;
  // 과거로 지나간 경우에도 항상 앞으로 연장되도록 max(now, 기존)
  const partyBaseMs = Math.max(partyExpireMs0, nowMs);
  const partyNewExpire = Timestamp.fromDate(new Date(partyBaseMs + days * MS_DAY));

  await setDoc(
    partyRef,
    {
      expireAt: partyNewExpire,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
    },
    { merge: true }
  );

  // 2) 서브컬렉션: 각 문서의 "자기 expireAt" 기준으로 +days
  async function bumpSubcolExpire(colRef, chunk = 300) {
    let cursor = null;
    while (true) {
      // 안정된 페이지네이션(문서 ID 기준)
      let qy = query(colRef, orderBy(documentId()), limit(chunk));
      if (cursor) qy = query(colRef, orderBy(documentId()), startAfter(cursor), limit(chunk));

      const snap = await getDocs(qy);
      if (snap.empty) break;

      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        const docExpireMs0 = toMillis(d.data()?.expireAt);
        // 없으면 파티의 기준시각을 사용하여 일괄 정렬감을 맞춤
        const baseMs = Math.max(docExpireMs0 ?? partyBaseMs, nowMs);
        const newExpire = Timestamp.fromDate(new Date(baseMs + days * MS_DAY));
        batch.set(d.ref, { expireAt: newExpire }, { merge: true });
      });
      await batch.commit();

      // 다음 페이지로 이동
      cursor = snap.docs[snap.docs.length - 1];
      if (snap.size < chunk) break;
    }
  }

  for (const name of cols) {
    await bumpSubcolExpire(collection(partyRef, name));
  }

  return { expireAt: partyNewExpire.toDate(), cols };
}


// firebaseClient.js 에 추가
export function subscribePartyAveragesPinnedFirst(code, cb, total = 20) {
  const db = getFirestore();
  const upper = String(code || "").toUpperCase();
  const col = collection(db, "parties", upper, "averages");

  // 안전장치
  if (!total || total < 1) {
    return () => {};
  }
  const latestTake = Math.max(0, total - 1);

  let pinned = null;     // 가장 오래된 1개
  let latest = [];       // 최신 N(=total-1)개, desc 정렬

  const emit = () => {
    const out = [];
    if (pinned) out.push(pinned);
    const deduped = pinned
      ? latest.filter(d => d.id !== pinned.id)
      : latest;
    out.push(...deduped.slice(0, latestTake));
    cb(out);
  };

  // 1) 가장 오래된 1개(최초 저장본) 실시간 구독
  const unsubPinned = onSnapshot(
    query(col, orderBy("createdAt", "asc"), limit(1)),
    (snap) => {
      pinned = snap.docs.length
        ? { id: snap.docs[0].id, ...snap.docs[0].data() }
        : null;
      emit();
    }
  );

  // 2) 최신 (total-1)개 실시간 구독
  const unsubLatest = onSnapshot(
    query(col, orderBy("createdAt", "desc"), limit(latestTake)),
    (snap) => {
      latest = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      emit();
    }
  );

  return () => {
    try { unsubPinned && unsubPinned(); } catch {}
    try { unsubLatest && unsubLatest(); } catch {}
  };
}


// NEW: Partypage(Lite) 전용 저장 래퍼 — BA와 동일 스키마로 저장하되 source만 'party'로.
// firebaseClient.js 내 기존 saveRaidCloudFromParty 교체

export async function saveRaidCloudFromParty(input) {
  const auth = getAuth();
  const uid = auth?.currentUser?.uid;
  if (!uid) throw new Error('로그인 필요');

  // BA의 중복 기준 재사용: raidName + timestamp + battleTime
  const raidName = input?.raidName || '';
  const timestamp = input?.timestamp || '';
  const battleTime = input?.battleTime || '';

  if (!raidName || !timestamp || !battleTime) {
    throw new Error('필수 필드 누락(raidName/timestamp/battleTime)');
  }

  const db = getFirestore();

  // (선택) 빠른 중복 체크
  const dupQ = query(
    collection(db, 'users', uid, 'raids'),
    where('raidName', '==', raidName),
    where('timestamp', '==', timestamp),
    where('battleTime', '==', battleTime),
    limit(1)
  );
  const dupSnap = await getDocs(dupQ);

  // BA와 동일 필드셋(알고 있는 공통 필드 위주)
  const newRecord = {
    raidName,
    timestamp,
    battleTime,
    mainTraits: input?.mainTraits || [],
    additionalTraits: input?.additionalTraits || [],
    job: input?.job || '',
    jobEngraving: input?.jobEngraving || '',
    level: input?.level ?? '',
    power: input?.power ?? '',
    memo: (input?.memo || '').trim(),
    // 메타
    ownerUid: uid,
    source: 'party',               // BA와 구분용 태그
    partyCode: input?.partyCode || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  let docId;
  let alreadyExists = false;

  if (!dupSnap.empty) {
    // 이미 있는 개인 클라우드 레코드 → 해당 문서 갱신(메모/레벨/파워 등 최신화)
    const docRef = dupSnap.docs[0].ref;
    docId = dupSnap.docs[0].id;
    alreadyExists = true;

    await setDoc(
      docRef,
      {
        ...newRecord,
        // createdAt은 최초값 유지되도록 merge 시 updatedAt만 변경
        createdAt: dupSnap.docs[0].data()?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } else {
    // 신규 생성
    const colRef = collection(db, 'users', uid, 'raids');
    const added = await addDoc(colRef, newRecord);
    docId = added.id;
  }

  // ★ 공개 집계 컬렉션에도 동일 키로 동기화 → 통계에서 집계 가능
  // BA와 동일 규칙: `${uid}_${docId}`
  const pubId = `${uid}_${docId}`;
  await setDoc(
    doc(db, 'raids_public', pubId),
    {
      ...newRecord,
      originUserId: uid,
      originRaidId: docId,
      // 공개 문서의 createdAt/updatedAt도 관리
      createdAt: alreadyExists
        ? (await getDoc(doc(db, 'raids_public', pubId))).data()?.createdAt || serverTimestamp()
        : serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { id: docId, alreadyExists };
}


export async function deleteUserAnalysesByKey(uid, { raidName, timestamp, battleTime }, chunk = 300) {
  const db = getFirestore();
  const colRef = collection(db, 'users', uid, 'analyses');

  // payload 필드 안에 저장했던 동일 키로 조회
  // 인덱스 필요할 수 있음: payload.raidName + payload.timestamp + payload.battleTime
  while (true) {
    const qy = query(
      colRef,
      qWhere('payload.raidName', '==', raidName || ''),
      qWhere('payload.timestamp', '==', timestamp || ''),
      qWhere('payload.battleTime', '==', battleTime || ''),
      limit(chunk)
    );
    const snap = await getDocs(qy);
    if (snap.empty) break;

    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();

    if (snap.size < chunk) break;
  }
}

/**
 * (옵션) 내 analyses 전체 삭제 도우미 - 유지보수/정리용
 */
export async function deleteAllUserAnalyses(uid, chunk = 300) {
  const db = getFirestore();
  const colRef = collection(db, 'users', uid, 'analyses');
  while (true) {
    const snap = await getDocs(query(colRef, limit(chunk)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    if (snap.size < chunk) break;
  }
}


/* ============================
 *  인덱스 권장 사항 (참고)
 *  - subscribeMyParties: memberUids CONTAINS + status == active + updatedAt DESC
 *  - fetchPartyRecords: payload.type == '__rec__' + createdAt DESC
 * ============================ */
